// src/pages/Archive/Misc.jsx
import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Stack,
  Avatar,
  alpha,
  useTheme
} from '@mui/material';
import {
  Description,
  Image,
  VideoLibrary,
  AttachFile,
  Archive,
  Download
} from '@mui/icons-material';
import { useI18n } from '../../i18n/i18n';

const MiscCard = ({ title, description, icon: Icon, color = "primary", count = 0 }) => {
  const theme = useTheme();
  
  return (
    <Card sx={{ 
      height: '100%',
      border: `2px solid ${alpha(theme.palette[color].main, 0.1)}`,
      borderRadius: 3
    }}>
      <CardContent sx={{ p: 3, height: '100%', textAlign: 'center' }}>
        <Avatar sx={{ 
          bgcolor: alpha(theme.palette[color].main, 0.1), 
          width: 80, 
          height: 80,
          mx: 'auto',
          mb: 2,
          border: `2px solid ${alpha(theme.palette[color].main, 0.2)}`
        }}>
          <Icon sx={{ color: theme.palette[color].main, fontSize: 40 }} />
        </Avatar>
        
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
          {title}
        </Typography>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {description}
        </Typography>
        
        {count > 0 && (
          <Typography variant="h6" color="primary.main" sx={{ mb: 2 }}>
            {count} archivos
          </Typography>
        )}
        
        <Button
          variant="outlined"
          color={color}
          fullWidth
          startIcon={<Download />}
          disabled={count === 0}
        >
          {count > 0 ? 'Explorar' : 'Próximamente'}
        </Button>
      </CardContent>
    </Card>
  );
};

const Misc = () => {
  const { t } = useI18n();
  const theme = useTheme();

  // Додаємо функцію для fallback перекладів
  const tt = (key, fallback) => {
    const translation = t(key);
    return translation === key ? fallback : translation;
  };

  const miscSections = [
    {
      title: 'Documentación',
      description: 'Manuales, procedimientos y documentación técnica',
      icon: Description,
      color: 'primary',
      count: 0
    },
    {
      title: 'Imágenes',
      description: 'Fotos de equipos, instalaciones y procesos',
      icon: Image,
      color: 'secondary',
      count: 0
    },
    {
      title: 'Videos',
      description: 'Grabaciones de procedimientos y entrenamientos',
      icon: VideoLibrary,
      color: 'info',
      count: 0
    },
    {
      title: 'Archivos Adjuntos',
      description: 'Documentos adicionales y archivos varios',
      icon: AttachFile,
      color: 'warning',
      count: 0
    },
    {
      title: 'Backups',
      description: 'Copias de seguridad y archivos de respaldo',
      icon: Archive,
      color: 'success',
      count: 0
    },
    {
      title: 'Plantillas',
      description: 'Formatos y plantillas reutilizables',
      icon: Description,
      color: 'error',
      count: 0
    }
  ];

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Archive />
        {tt('archive.misc.title', 'Otros Materiales')}
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" paragraph>
        {tt('misc.description', 'Aquí estarán otros tipos de informes y materiales')}
      </Typography>

      {/* Informational Banner */}
      <Card sx={{ 
        mb: 4,
        background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.1)} 0%, ${alpha(theme.palette.info.light, 0.05)} 100%)`,
        border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`
      }}>
        <CardContent>
          <Typography variant="h6" color="info.main" gutterBottom>
            🚧 Sección en Desarrollo
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Esta sección está actualmente en desarrollo. Próximamente estarán disponibles 
            todos los materiales adicionales y archivos misceláneos.
          </Typography>
        </CardContent>
      </Card>

      {/* Misc Sections Grid */}
      <Grid container spacing={3}>
        {miscSections.map((section, index) => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <MiscCard
              title={section.title}
              description={section.description}
              icon={section.icon}
              color={section.color}
              count={section.count}
            />
          </Grid>
        ))}
      </Grid>

      {/* Coming Soon Features */}
      <Card sx={{ mt: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Próximas Características
          </Typography>
          <Stack spacing={1}>
            <Typography variant="body2">
              • Subida y gestión de archivos multimedia
            </Typography>
            <Typography variant="body2">
              • Organización por categorías y etiquetas
            </Typography>
            <Typography variant="body2">
              • Búsqueda avanzada en documentos
            </Typography>
            <Typography variant="body2">
              • Compartir archivos con otros usuarios
            </Typography>
            <Typography variant="body2">
              • Versiones y historial de cambios
            </Typography>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Misc;


